import { Dialog, Transition } from '@headlessui/react';
import { Fragment, type ReactNode } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

type BrandModalProps = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  maxWidthClass?: string;
  zIndexClass?: string;
  panelClassName?: string;
};

export function BrandModal({
  open,
  onClose,
  children,
  maxWidthClass = 'max-w-lg',
  zIndexClass = 'z-50',
  panelClassName = '',
}: BrandModalProps) {
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className={`relative ${zIndexClass}`} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="modal-overlay" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={`modal-shell w-full ${maxWidthClass} transform text-left align-middle fade-up ${panelClassName}`}
              >
                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

/** Standard header close control — use on every modal header. */
export function ModalCloseButton({
  onClose,
  className = '',
}: {
  onClose: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClose}
      className={`brand-action-icon shrink-0 rounded-lg p-1 ${className}`}
      aria-label="Close"
    >
      <XMarkIcon className="h-5 w-5" />
    </button>
  );
}

type BrandModalHeaderProps = {
  title: ReactNode;
  subtitle?: string;
  /** Required — every branded modal exposes a header close control. */
  onClose: () => void;
};

export function BrandModalHeader({ title, subtitle, onClose }: BrandModalHeaderProps) {
  return (
    <div className="modal-header flex items-start justify-between gap-4">
      <div className="min-w-0 text-left">
        <Dialog.Title as="h3" className="gradient-title text-lg font-semibold sm:text-xl">
          {title}
        </Dialog.Title>
        {subtitle ? <p className="mt-1 text-sm text-slate-600">{subtitle}</p> : null}
      </div>
      <ModalCloseButton onClose={onClose} />
    </div>
  );
}

export function BrandModalBody({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`modal-body ${className}`}>{children}</div>;
}

export function BrandModalFooter({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`modal-footer ${className}`}>{children}</div>;
}
